import React, { FC, ReactNode, useEffect, useRef } from 'react'
import { FormikProps } from 'formik'
import clsx from 'clsx'
import { CustomField, CustomFieldSerialProps, TypeSerial } from '../../interfaces/Settings'
import useTodayRange from '../../hooks/TodayRange'
import { useIntl } from 'react-intl'
import { customNumberFormat, customNumberFormatString, handleCaps, isValidUrl } from '../../functions/general'
import InputAttachment from '../Input/InputAttachment'
import DatePicker from 'rsuite/esm/DatePicker'
import moment from 'moment-timezone'
import { nanoid } from '@reduxjs/toolkit'
import InputPhone from '../Input/InputPhone'
import { Merchant, SimpleMerchant } from '../../interfaces/Merchant'
import { User } from '../../interfaces/User'
import { Pipeline, SimplePipeline } from '../../interfaces/Pipeline'
import { SelectOption } from '../../interfaces/SelectOption'
import Select, { components } from "react-select";
import { isBefore, isAfter, subDays, startOfToday, addDays } from 'date-fns';
import VirtualizedSelect, { VirtualizedSelectOption, VirtualizedSelectOptionsType } from '../Select/SelectVirtualized'
import useUser from '../../hooks/User'
import { useCustomeStore } from '../../stores/CustomeStore'
import { CustomSelectMeetingMultipleValue, CustomSelectMeetingOption, CustomSelectPipelineOption, CustomSelectPipelineSingleValue } from '../Pipelines/CustomComponentSelect/CustomSelect'
import DateRangePicker from 'rsuite/esm/DateRangePicker'
import { TimePicker } from 'rsuite';
import { usePipelineStore } from '../../stores/PipelineStore'
import { useMerchantStore } from '../../stores/MerchantStore'
import { useUserStore } from '../../stores/UserStore'
import { useMeetingStore } from '../../stores/MeetingsStore'
import { Meeting, SimpleMeeting } from '../../interfaces/Meeting'
import { getSelectedValueCustomFieldMerchant, getSelectedValueCustomFieldPipeline, getSelectedValueCustomFieldMeeting } from '../../functions/helperCustomField'

export interface CompanyCustomCFProps {
    [companyName: string]: {
        [context: string]: {
            cf_keys: string[]
            disabled: boolean
            required: boolean
            value: string | number
        }
    }
}

interface RenderCustomFieldGeneralFormProps {
    customFieldGeneral: CustomField
    formik: FormikProps<any>
    errors: any
    containerRef?: HTMLElement | null
    dataCustomFieldSerial?: CustomFieldSerialProps
    handleGenerateSerial?: (key: string) => void
    typeAction?: 'add' | 'update'
    typeTemplateCustomField?: 'pipeline' | 'default'
    isCustomeFieldDateDisabled?: boolean
    MenuList?: (props: any) => JSX.Element
    inputRefCustomField?: React.MutableRefObject<(HTMLInputElement | null)[]>
    typeMeeting?: 'schedule' | 'report' | 'visit'
    companyCustom?: CompanyCustomCFProps | null
}

const RenderCustomFieldGeneralForm: FC<RenderCustomFieldGeneralFormProps> = ({ customFieldGeneral, formik, errors, containerRef, dataCustomFieldSerial, handleGenerateSerial, typeAction = 'add', typeTemplateCustomField = 'default', isCustomeFieldDateDisabled = false, MenuList, inputRefCustomField, typeMeeting, companyCustom }) => {
    const intl = useIntl()
    const todayRange = useTodayRange();
    const { simplePipelines, simplePipelineLoading } = usePipelineStore()
    const { simpleMerchants, simpleMerchantLoading } = useMerchantStore()
    const { simpleMeetings, simpleMeetingLoading } = useMeetingStore()
    const { users, userLoading } = useUserStore()

    const getError = (key: string) => {
        return errors?.[key]
    }

    const isTouched = (key: string) => {
        return formik.touched?.[key]
    }

    const handleLinkValidation = (e: React.FocusEvent<HTMLInputElement>, key: string) => {
        const value = e.target.value;
        if (value && !isValidUrl(value)) {
            formik.setFieldError(key, intl.formatMessage({ id: 'FORM.VALIDATION.MUST_LINK' }));
        }
    };


    const getIsRequired = (key: string) => {
        if (customFieldGeneral[key].required && !typeMeeting) {
            return true
        }
        else if (customFieldGeneral[key].required && typeMeeting === 'report') {
            return true
        }
        else return false
    }

    return (
        <div className="custom-fields-container">
            {customFieldGeneral && Object.keys(customFieldGeneral).map((key, index) => {
                if (customFieldGeneral[key].type === 'text') {
                    return (
                        <div key={key}>
                            <label
                                className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}
                            >
                                {customFieldGeneral[key].name}
                            </label>
                            <div className='input-group mb-3'>
                                <input
                                    {...(companyCustom && companyCustom.wolacom && companyCustom.wolacom.text.cf_keys.includes(key) && companyCustom.wolacom.text.disabled && { disabled: true })}
                                    placeholder={customFieldGeneral[key].name}
                                    value={formik.values[key] || ''}
                                    // {...formik.getFieldProps(key)}
                                    onChange={(e) => {
                                        formik.setFieldValue(key, e.target.value)
                                    }}
                                    className={clsx('form-control form-control-lg', {
                                        'is-invalid': isTouched(key) && getError(key),
                                    })}
                                    type='text'
                                    name={key}
                                    autoComplete='off'
                                    style={{ zIndex: 0 }}
                                    required={customFieldGeneral[key].required}
                                    onInput={(e) => handleCaps(e)}
                                    onBlur={() => {
                                        formik.setFieldTouched(key, true);
                                    }}
                                    ref={(ref) => {
                                        if (inputRefCustomField?.current) {
                                            inputRefCustomField.current[index] = ref
                                        }
                                    }}
                                />
                            </div>
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                } else if (customFieldGeneral[key].type === 'link') {
                    return (
                        <div key={index}>
                            <label className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>
                                {customFieldGeneral[key].name}
                            </label>
                            <div className="input-group mb-3">
                                <input
                                    placeholder={customFieldGeneral[key].name}
                                    {...formik.getFieldProps(key)}
                                    className={clsx(
                                        'form-control form-control-lg txt-link',
                                        { 'is-invalid': isTouched(key) && getError(key) },
                                    )}
                                    value={formik.values[key] || ''}
                                    type='text'
                                    name={key}
                                    autoComplete='off'
                                    onBlur={(e) => {
                                        formik.setFieldTouched(key, true);
                                        handleLinkValidation(e, key);
                                    }}
                                    style={{ zIndex: 0 }}
                                    required={customFieldGeneral[key].required}
                                    ref={(ref) => {
                                        if (inputRefCustomField?.current) {
                                            inputRefCustomField.current[index] = ref
                                        }
                                    }}
                                />
                            </div>
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                } else if (customFieldGeneral[key].type === 'serial') {
                    return (
                        <div key={index}>
                            <label className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>{customFieldGeneral[key].name}</label>
                            {
                                dataCustomFieldSerial?.[key]?.loading ?
                                    <span className="text-white bg-active-success active py-1 px-2 fs-8 border-md rounded" style={{ float: "inline-end" }}>
                                        <l-tailspin
                                            size="15"
                                            stroke="3"
                                            speed="0.9"
                                            color="#ffffff"
                                        ></l-tailspin>
                                    </span>
                                    :
                                    <span className="text-white bg-active-success active py-1 px-2 fs-8 border-md rounded" onClick={() => handleGenerateSerial?.(key)} style={{ float: "inline-end", cursor: "pointer" }}>{intl.formatMessage({ id: 'FORM.ACTION.GENERATE_SERIAL' })}</span>
                            }
                            <div className="d-flex mb-3 gap-3">
                                <input
                                    disabled
                                    id={key}
                                    placeholder={customFieldGeneral[key].name}
                                    {...formik.getFieldProps(key)}
                                    value={typeof formik.values[key as keyof typeof formik.values] === 'string' ? formik.values[key as keyof typeof formik.values] : (formik.values[key as keyof typeof formik.values] as any)?.join("")}
                                    className={clsx('form-control form-control-lg')}
                                    type='text'
                                    name={key}
                                    autoComplete='off'
                                    style={{ zIndex: 0 }}
                                    ref={(ref) => {
                                        if (inputRefCustomField?.current) {
                                            inputRefCustomField.current[index] = ref
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    )
                } else if (customFieldGeneral[key].type === 'image') {
                    return (
                        <div key={key} className='col-md-12 mb-3'>
                            {/* Attachments */}
                            <span className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>{customFieldGeneral[key].name}</span><br />
                            <small><i>*Format PNG, JPG, JPEG</i></small>
                            <InputAttachment key={key} keyString={key} formik={formik} isMulti={false} type="input" accept="image/png, image/jpeg, image/jpg" typeAction={typeAction} />
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                } else if (customFieldGeneral[key].type === 'images') {
                    return (
                        <div key={key} className='col-md-12 mb-3'>
                            {/* Attachments */}
                            <span className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>{customFieldGeneral[key].name}</span><br />
                            <small><i>*Format PNG, JPG, JPEG</i></small>
                            <InputAttachment key={key} keyString={key} formik={formik} isMulti={true} type="input" accept="image/png, image/jpeg, image/jpg" typeAction={typeAction} />
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                } else if (customFieldGeneral[key].type === 'file') {
                    return (
                        <div key={key} className="col-md-12 mb-3">
                            <span className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>
                                {customFieldGeneral[key].name}
                            </span><br />
                            <small><i>*Format PDF, XLS, XLSX, DOCX, PPT</i></small>
                            <InputAttachment
                                key={key}
                                keyString={key}
                                formik={formik}
                                isMulti={false}
                                type="input"
                                accept="application/pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                typeAction={typeAction}
                            />
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                } else if (customFieldGeneral[key].type === 'files') {
                    return (
                        <div key={key} className="col-md-12 mb-3">
                            <span className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>
                                {customFieldGeneral[key].name}
                            </span><br />
                            <small><i>*Format PDF, XLS, XLSX, DOCX, PPT</i></small>
                            <InputAttachment
                                key={key}
                                keyString={key}
                                formik={formik}
                                isMulti={true}
                                type="input"
                                accept="application/pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                typeAction={typeAction}
                            />
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                } else if (customFieldGeneral[key].type === 'date' && typeTemplateCustomField === 'default') {
                    return (
                        <div key={index} className='mb-3'>
                            <label className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>
                                {customFieldGeneral[key].name}
                            </label>
                            <div className='input-group'>
                                <DatePicker
                                    ranges={todayRange}
                                    oneTap
                                    editable={false}
                                    format="dd MMM yyyy"
                                    value={formik.values[key as keyof typeof formik.values] ? new Date(formik.values[key as keyof typeof formik.values]) : null}
                                    defaultValue={null}
                                    style={{ width: "100%", zIndex: 0 }}
                                    container={containerRef as HTMLElement}
                                    onChange={(value: any) => {
                                        if (value) formik.setFieldValue(key, moment(value).format('YYYY-MM-DD'))
                                        else formik.setFieldValue(key, null)
                                    }}
                                />
                            </div>
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                } else if (customFieldGeneral[key].type === 'date' && typeTemplateCustomField === 'pipeline') {
                    var min: number | null = null
                    var max: number | null = null

                    if (customFieldGeneral[key].min === undefined || customFieldGeneral[key].min === null) min = null //Tidak ada batasan
                    else if (customFieldGeneral[key].min) min = Number(customFieldGeneral[key].min)
                    else min = 0 //Batasan hari ini

                    if (customFieldGeneral[key].max === undefined || customFieldGeneral[key].max === null) max = null //Tidak ada batasan
                    else if (customFieldGeneral[key].max) max = Number(customFieldGeneral[key].max)
                    else max = 0 //Batasan hari ini

                    return (
                        <>
                            {/* Date */}
                            <label key={key} className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>{customFieldGeneral[key].name}</label>
                            <div className="input-group mb-3">
                                <DatePicker
                                    disabled={isCustomeFieldDateDisabled}
                                    ranges={todayRange}
                                    oneTap
                                    editable={false}
                                    format="dd MMM yyyy"
                                    value={formik.values[key as keyof typeof formik.values] ? new Date(formik.values[key as keyof typeof formik.values]) : null}
                                    defaultValue={null}
                                    style={{ width: "100%", zIndex: 0 }}
                                    container={containerRef as HTMLElement}
                                    onChange={(value: any) => {
                                        if (value) formik.setFieldValue(key, moment(value).format('YYYY-MM-DD'))
                                        else formik.setFieldValue(key, null)
                                    }}
                                    shouldDisableDate={date => {
                                        var startDate = min === null ? null : min > 0 ? addDays(new Date(), min) : subDays(new Date(), min * -1);
                                        var endDate = max === null ? null : max > 0 ? addDays(new Date(), max) : subDays(new Date(), max * -1);

                                        date.setHours(0, 0, 0, 0);
                                        if (startDate !== null && endDate !== null) {
                                            startDate.setHours(0, 0, 0, 0);
                                            endDate.setHours(0, 0, 0, 0);
                                            return date > endDate || date < startDate
                                        } else if (startDate !== null && endDate === null) {
                                            startDate.setHours(0, 0, 0, 0);
                                            return date < startDate
                                        } else if (startDate === null && endDate !== null) {
                                            endDate.setHours(0, 0, 0, 0);
                                            return date > endDate
                                        } else return false
                                    }}
                                />
                            </div>
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </>
                    )
                } else if (customFieldGeneral[key].type === 'option') {
                    const options = customFieldGeneral[key].value.map((option: string | TypeSerial) => ({
                        value: option,
                        label: option,
                    }))

                    return (
                        <div key={index}>
                            <label className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>
                                {customFieldGeneral[key].name}
                            </label>
                            <div className='input-group mb-3'>
                                <Select
                                    key={nanoid()}
                                    isClearable
                                    options={options}
                                    placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${customFieldGeneral[key].name}`}
                                    value={options.find(option => option.value === formik.values[key as keyof typeof formik.values]) || null}
                                    onChange={option => formik.handleChange({
                                        target: {
                                            name: key,
                                            value: option?.value || ""
                                        }
                                    })}
                                    onBlur={
                                        () => formik.values[key as keyof typeof formik.values] === "" && formik.setFieldTouched(key, true)
                                    }
                                    className='w-100'
                                />
                            </div>
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                } else if (customFieldGeneral[key].type === 'multiple') {
                    return (
                        <div key={index}>
                            <label className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>
                                {customFieldGeneral[key].name}
                            </label>
                            <div className='input-group mb-3'>
                                <div className='form-check form-check-custom form-check-solid' style={{ display: 'contents' }}>
                                    {
                                        customFieldGeneral[key].value.map((option: string | TypeSerial, index: number) => {
                                            return (
                                                <div style={{ display: 'flex', padding: "3px", overflowWrap: "anywhere" }} key={index}>
                                                    <input
                                                        key={index}
                                                        {...formik.getFieldProps(key)}
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        value={option as string}
                                                        name={key}
                                                        checked={formik.values[key]?.includes(option)}
                                                    />
                                                    <label className="form-check-label me-2">{option}</label>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            </div>
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                } else if (customFieldGeneral[key].type === 'phone') {
                    return (
                        <div key={index} className='mb-3'>
                            <label className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>
                                {customFieldGeneral[key].name}
                            </label>
                            <InputPhone
                                {...(companyCustom && companyCustom.wolacom && companyCustom.wolacom.text.cf_keys.includes(key) && companyCustom.wolacom.text.disabled && { disabled: true })}
                                keyString={key}
                                formik={formik}
                                type="input"
                            />
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                } else if (customFieldGeneral[key].type === 'number') {
                    return (
                        <div key={index} className='mb-3'>
                            <label className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>
                                {customFieldGeneral[key].name}
                            </label>
                            <input
                                type="text"
                                name={key}
                                value={formik.values[key] ? customNumberFormatString(formik.values[key].toString()) : ''}
                                onBlur={formik.handleBlur}
                                onChange={(e) => {
                                    const numericValue = e.target.value.replace(/\D/g, '');
                                    if (numericValue === '') {
                                        formik.setFieldValue(key, '');
                                        return;
                                    }
                                    formik.setFieldValue(key, Number(numericValue));
                                }}
                                className={clsx('form-control form-control-lg', {
                                    'is-invalid': isTouched(key) && getError(key),
                                })}
                                ref={(ref) => {
                                    if (inputRefCustomField?.current) {
                                        inputRefCustomField.current[index] = ref
                                    }
                                }}
                            />
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                } else if (customFieldGeneral[key].type === 'merchant') {
                    const filteredMerchants = getSelectedValueCustomFieldMerchant(customFieldGeneral, simpleMerchants, key)

                    const optionsSimpleMerchant = filteredMerchants.map((option: SimpleMerchant) => ({
                        value: option.id,
                        label: option.name
                    }))

                    return (
                        <>
                            <label key={key} className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>{customFieldGeneral[key].name}</label>
                            <div className="input-group mb-3">
                                <VirtualizedSelect
                                    options={optionsSimpleMerchant as VirtualizedSelectOptionsType[]}
                                    isLoading={simpleMerchantLoading}
                                    isDisabled={simpleMerchantLoading}
                                    className="w-100"
                                    placeholder={`${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} Merchant`}
                                    value={optionsSimpleMerchant?.filter(option => formik.values[key]?.includes(option.value)) as VirtualizedSelectOption[]}
                                    onChange={(selectedOptions) =>
                                        formik.setFieldValue(
                                            key,
                                            selectedOptions ? selectedOptions.map((option: VirtualizedSelectOption) => option.value) : []
                                        )
                                    }
                                    onBlur={() => {
                                        if (formik.values[key] === "") {
                                            formik.setFieldTouched(key, true);
                                        }
                                    }}
                                    components={{ MenuList }}
                                    isMulti
                                />
                            </div>
                            {
                                getError(key) &&
                                (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{getError(key)}</span>
                                    </div>
                                )
                            }
                        </>
                    )
                } else if (customFieldGeneral[key].type === 'user') {
                    const optionsUser = users?.map((option: User) => ({
                        value: option.id,
                        label: option.name
                    }))

                    return (
                        <>
                            <label key={key} className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>{customFieldGeneral[key].name}</label>
                            <div className="input-group mb-3">
                                <VirtualizedSelect
                                    options={optionsUser as VirtualizedSelectOptionsType[]}
                                    isLoading={userLoading}
                                    isDisabled={userLoading}
                                    className="w-100"
                                    placeholder={`${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} User`}
                                    value={optionsUser?.filter(option => formik.values[key]?.includes(option.value)) as VirtualizedSelectOption[]}
                                    onChange={(selectedOptions) =>
                                        formik.setFieldValue(
                                            key,
                                            selectedOptions ? selectedOptions.map((option: VirtualizedSelectOption) => option.value) : []
                                        )
                                    }
                                    onBlur={() => {
                                        if (formik.values[key] === "") {
                                            formik.setFieldTouched(key, true);
                                        }
                                    }}
                                    components={{ MenuList }}
                                    isMulti
                                />
                            </div>
                            {
                                getError(key) &&
                                (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{getError(key)}</span>
                                    </div>
                                )
                            }
                        </>
                    )
                } else if (customFieldGeneral[key].type === 'pipeline') {
                    const filteredPipelines = getSelectedValueCustomFieldPipeline(customFieldGeneral, simplePipelines, key)

                    const optionsSimplePipeline = filteredPipelines.map((option: SimplePipeline) => ({
                        ...option,
                        value: option.id,
                        label: option.name!
                    }))

                    return (
                        <>
                            <label key={key} className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>{customFieldGeneral[key].name}</label>
                            <div className="input-group mb-3">
                                <VirtualizedSelect
                                    options={optionsSimplePipeline as VirtualizedSelectOptionsType[]}
                                    isLoading={simplePipelineLoading}
                                    isDisabled={simplePipelineLoading}
                                    className="w-100"
                                    placeholder={`${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} Pipeline`}
                                    value={optionsSimplePipeline?.find(option => option.value === formik.values[key]) || null}
                                    onChange={(selectedOption) => {
                                        formik.setFieldValue(
                                            key,
                                            selectedOption ? selectedOption.value : null
                                        )
                                    }
                                    }
                                    onBlur={() => {
                                        if (formik.values[key] === "") {
                                            formik.setFieldTouched(key, true);
                                        }
                                    }}
                                    isMulti={false}
                                    components={{
                                        Option: CustomSelectPipelineOption,
                                        SingleValue: CustomSelectPipelineSingleValue
                                    }}
                                />
                            </div>
                            {
                                getError(key) &&
                                (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{getError(key)}</span>
                                    </div>
                                )
                            }
                        </>
                    )
                } else if (customFieldGeneral[key].type === 'meeting') {
                    const filteredMeetings = getSelectedValueCustomFieldMeeting(customFieldGeneral, simpleMeetings, key)
                    const optionSimpleMeeting = filteredMeetings.map((option: SimpleMeeting) => ({
                        ...option,
                        value: option.id,
                        label: option.name!
                    }))
                    return (
                        <>
                            <label key={key} className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>{customFieldGeneral[key].name}</label>
                            <div className="input-group mb-3">
                                <VirtualizedSelect
                                    options={optionSimpleMeeting as VirtualizedSelectOptionsType[]}
                                    isLoading={simpleMeetingLoading}
                                    isDisabled={simpleMeetingLoading}
                                    className="w-100"
                                    placeholder={`${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} Meeting`}
                                    value={optionSimpleMeeting?.filter(option => formik.values[key]?.includes(option.value)) as VirtualizedSelectOption[]}
                                    onChange={(selectedOptions) =>
                                        formik.setFieldValue(
                                            key,
                                            selectedOptions ? selectedOptions.map((option: VirtualizedSelectOption) => option.value) : []
                                        )
                                    }
                                    onBlur={() => {
                                        if (formik.values[key] === "") {
                                            formik.setFieldTouched(key, true);
                                        }
                                    }}
                                    isMulti
                                    components={{
                                        Option: CustomSelectMeetingOption,
                                    }}
                                    isMultiValue
                                    typeMultiValue="meeting"
                                />
                            </div>
                            {
                                getError(key) &&
                                (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{getError(key)}</span>
                                    </div>
                                )
                            }
                        </>
                    )
                } else if (customFieldGeneral[key].type === 'dateTimeRange') {
                    const formatDateRange = (value: any) => {
                        if (Array.isArray(value)) {
                            return value.map((item: any) => new Date(item))
                        }
                        return value
                    }
                    return (
                        <div key={key} className='mb-3'>
                            <label key={key} className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>
                                {customFieldGeneral[key].name}
                            </label>
                            <div className='input-group'>
                                <DateRangePicker
                                    format="dd MMM yyyy HH:mm"
                                    value={formatDateRange(formik.values[key])}
                                    defaultValue={null}
                                    style={{ width: "100%", zIndex: 0 }}
                                    container={containerRef as HTMLElement}
                                    onChange={(value: any) => {
                                        formik.setFieldValue(key, value)
                                    }}
                                />
                            </div>
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                } else if (customFieldGeneral[key].type === 'time') {
                    const formatDate = (value: any) => {
                        if (!value) return null;
                        if (typeof value === 'string') {
                            return new Date(value);
                        }
                        if (value instanceof Date) {
                            return value;
                        }

                        return null;
                    }

                    return (
                        <div key={key} className='mb-3'>
                            <label key={key} className={`form-label fs-6 fw-bolder text-dark ${getIsRequired(key) ? 'required' : ''}`}>
                                {customFieldGeneral[key].name}
                            </label>
                            <div className='input-group'>
                                <TimePicker
                                    format="HH:mm"
                                    value={formatDate(formik.values[key])}
                                    defaultValue={null}
                                    style={{ width: "100%", zIndex: 0 }}
                                    container={containerRef as HTMLElement}
                                    onChange={(value: any) => {
                                        formik.setFieldValue(key, value ? value.toISOString() : null)
                                    }}
                                />
                            </div>
                            {getError(key) && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className='text-danger'>
                                        {getError(key)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )
                }
            })}
        </div>
    )
}

export default RenderCustomFieldGeneralForm